import { OverlayContainer } from '@angular/cdk/overlay';
import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import {
  TitleStrategy,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { provideAuth } from '@sisoma/auth';
import { provideMedia } from '@sisoma/media/providers';
import { provideUiLayout } from '@sisoma/ui';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { PartnerTitleStrategy } from './core/navigation/title-strategy';
import { DialogAwareOverlayContainer } from './core/overlay/dialog-aware-overlay-container';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    // The HTTP + auth layer (fp1-03). This also registers `HttpClient` via
    // `provideHttpClient(withFetch(), withInterceptors([authInterceptor]))`, so
    // do NOT add `provideHttpClient` separately. `forbiddenUrl` targets the
    // local 403 route; `loginUrl`/`apiUrl` come from the build environment.
    provideAuth({
      apiUrl: environment.apiUrl,
      loginUrl: environment.loginUrl,
      forbiddenUrl: '/forbidden',
      // A capability denial renders *inside* the shell, where `MembershipGate`
      // can tell a roleless member why their capability set is empty instead of
      // handing them a generic 403. `/forbidden` stays outside it for the
      // partner-access denial, which has to — see the route table.
      capabilityDeniedUrl: '/no-access',
    }),
    // The media picker's base URL. Same `apiUrl` as `provideAuth` above, and it
    // has to be: `authInterceptor` only attaches credentials to requests under
    // its own `apiUrl`, so a divergence here would silently strip the cookie off
    // every media call. This registers config only — the two media services stay
    // component-provided (MediaPicker provides MediaLibraryService, the content
    // drawer provides CloudinaryUploadService), so nothing becomes a singleton.
    // Imported from `@sisoma/media/providers`, not the `@sisoma/media` barrel,
    // because this file is eager: the barrel here would put the picker, which
    // only lazy routes draw, into the initial bundle.
    provideMedia({ apiUrl: environment.apiUrl }),
    // Namespaced so the sidenav-collapsed setting is not shared with the
    // other SISOMA apps served from the same origin.
    provideUiLayout({ storageKey: 'partner-area.sidenav-collapsed' }),
    provideRouter(
      routes,
      // Route `data`/params bind straight to component inputs (see PlaceholderPage).
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),
    { provide: TitleStrategy, useClass: PartnerTitleStrategy },
    // CDK overlays must follow the app into the content drawer's native modal
    // <dialog> (top layer + inert would otherwise bury every dropdown panel
    // opened from inside it) — see the class doc.
    { provide: OverlayContainer, useClass: DialogAwareOverlayContainer },
  ],
};
